import { useState, useMemo } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router'
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Box, Chip, Divider } from '@mui/material'
import { ExpandMore, Check } from '@mui/icons-material'

import { Button as AppButton } from '..'
import { queries } from '../../graphql'
import { useBranding } from '../../hooks'
import { computeContexts, detectActiveContext } from './compute-contexts'
import { CreateFundDialog } from './create-fund-dialog'
import { LABEL_KEYS, DEFAULT_LABELS } from './labels'

// FLIGHT-768: long portal/fund names get cut off mid-word by CSS ellipsis alone,
// so truncate to a fixed character count before render for a consistent cutoff.
const BUTTON_TEXT_MAX_LENGTH = 15

function truncateButtonText(name) {
  if (!name || name.length <= BUTTON_TEXT_MAX_LENGTH) return name
  return `${name.slice(0, BUTTON_TEXT_MAX_LENGTH)}…`
}

/**
 * FLIGHT-369 fund-context switcher.
 *
 * One user, possibly many fund/charity/admin role assignments. Switching to
 * another context = soft re-login: clear permissions cache, refetch role
 * data, navigate to the target context's home. Active fund's role drives
 * the menu shell via the existing useMenu(menuType) flow.
 *
 * Hidden when the user has 0 or 1 contexts to switch between.
 *
 * All user-facing strings resolve through useBranding().getLabelValue, so
 * each branding instance can rename "Donor", "Advisor", "Switch", etc. via
 * the existing Branding Labels admin UI.
 */
export function ContextSwitcher() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const apolloClient = useApolloClient()
  const branding = useBranding()
  const [anchorEl, setAnchorEl] = useState(null)
  const [createFundOpen, setCreateFundOpen] = useState(false)

  const { data: meData } = useQuery(queries.user.me)

  const contexts = useMemo(() => computeContexts({ me: meData?.me }), [meData?.me])
  const active = detectActiveContext(pathname)
  const effectiveActive =
    active ??
    contexts.find((c) => c.kind === 'fund') ??
    contexts.find((c) => c.kind === 'charity') ??
    contexts.find((c) => c.kind === 'advisor') ??
    null
  const activeContext =
    contexts.find(
      (c) => effectiveActive && c.kind === effectiveActive.kind && String(c.id) === String(effectiveActive.id)
    ) || null

  const t = (key) => branding.getLabelValue(key, DEFAULT_LABELS[key])

  if (contexts.length === 0) return null

  const handleOpen = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const handleSwitch = async (target) => {
    setAnchorEl(null)
    if (activeContext && target.kind === activeContext.kind && String(target.id) === String(activeContext.id)) {
      return
    }
    // Permissions are user-scoped (queried by userId), not portal-scoped, so they
    // don't change on a same-user portal switch. Clearing here causes the admin
    // sidebar to mount with a cold cache and hide all items during the re-fetch.
    try {
      await apolloClient.refetchQueries({
        include: [queries.user.me, queries.funds.myFunds, queries.funds.myFund, queries.funds.myAccessibleFunds],
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ContextSwitcher] refetch failed:', err)
    }
    navigate(target.homeRoute)
  }

  const handleFundCreated = async (fund) => {
    setCreateFundOpen(false)
    try {
      await apolloClient.refetchQueries({
        include: [queries.user.me, queries.funds.myFunds, queries.funds.myFund, queries.funds.myAccessibleFunds],
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ContextSwitcher] refetch failed:', err)
    }
    navigate(`/funds/${fund.id}`)
  }

  const buttonText = truncateButtonText(activeContext?.displayName) ?? t(LABEL_KEYS.BUTTON)

  return (
    <Box sx={{ mr: 1 }}>
      <Button
        variant="text"
        onClick={handleOpen}
        data-testid="contextSwitcherButton"
        endIcon={<ExpandMore fontSize="small" />}
        sx={{
          color: 'primary.contrastText',
          fontSize: '0.875rem',
          textTransform: 'none',
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': { color: 'primary.contrastText', opacity: 0.85 },
        }}
      >
        {buttonText}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        <MenuItem disabled sx={{ opacity: '0.6 !important' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {t(LABEL_KEYS.MENU_HEADER)}
          </Typography>
        </MenuItem>
        {contexts.map((ctx) => {
          const isActive =
            activeContext && ctx.kind === activeContext.kind && String(ctx.id) === String(activeContext.id)
          return (
            <MenuItem
              key={`${ctx.kind}-${ctx.id ?? 'system'}`}
              onClick={() => handleSwitch(ctx)}
              selected={!!isActive}
              data-testid={`contextSwitcherItem-${ctx.kind}-${ctx.id ?? 'system'}`}
              sx={{
                alignItems: 'flex-start',
                py: 1,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
                '&.Mui-selected': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.18)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, alignSelf: 'center' }}>
                {isActive && <Check fontSize="small" sx={{ color: 'text.primary' }} />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {ctx.displayName}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Chip size="small" label={t(ctx.roleLabelKey)} sx={{ height: 20, fontSize: '0.7rem' }} />
                    {ctx.fundNumber && (
                      <Typography variant="caption" color="text.secondary">
                        {ctx.fundNumber}
                      </Typography>
                    )}
                    {ctx.hasDonorTaskAccess && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label="Donor-task access"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
              />
            </MenuItem>
          )
        })}
        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.7)', borderBottomWidth: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1.5 }}>
          <AppButton
            onClick={() => {
              handleClose()
              setCreateFundOpen(true)
            }}
            data-testid="contextSwitcherCreateFund"
            size="small"
            fullWidth
          >
            Create new giving fund
          </AppButton>
        </Box>
      </Menu>
      <CreateFundDialog
        open={createFundOpen}
        onClose={() => setCreateFundOpen(false)}
        me={meData?.me}
        onCreated={handleFundCreated}
      />
    </Box>
  )
}
